import { Component, EventEmitter, Input, OnInit, Output, ViewChild, OnDestroy } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { TranslateModule } from "@ngx-translate/core";
import { AppSettings } from "app/modules/shared/app.settings";
import { MiFormComponent } from "app/modules/shared/components/mi-form/mi-form.component";
import type { AttributeData } from "app/modules/shared/models/attribute.models";
import { Country } from "app/modules/shared/models/country";
import { Language } from "app/modules/shared/models/language";
import { CommonBindingDataService } from "app/modules/shared/services/common-binding-data.service";
import { ConfigService } from "app/modules/shared/services/config.service";
import { EntityService } from "app/modules/shared/services/entity.service";
import { MetaDataService } from "app/modules/shared/services/meta-data.service";
import { RestApiService } from "app/modules/shared/services/rest-api.service";
import * as _ from 'lodash';
import { MenuItem, MessageService } from "primeng/api";
import { BreadcrumbModule } from "primeng/breadcrumb";
import { ButtonModule } from "primeng/button";
import { DialogModule } from "primeng/dialog";
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SkeletonModule } from 'primeng/skeleton';
import { TabViewModule } from "primeng/tabview";

@Component({
  selector: 'app-add-protocol-site',
  standalone: true,
  imports: [
    BreadcrumbModule,
    TabViewModule,
    MiFormComponent,
    DialogModule,
    ButtonModule,
    TranslateModule,
    ProgressSpinnerModule,
    SkeletonModule
  ],
  templateUrl: './add-protocol-site.component.html',
  styleUrl: './add-protocol-site.component.scss'
})
export class AddProtocolSiteComponent implements OnInit, OnDestroy {
  activeIndex: number = 0;
  data: AttributeData;
  protocolSiteAttributeData: any;
  originalProtocolSiteAttributeData: any;
  routePath: MenuItem[] = [];
  attributeLabels: any;
  moduleName: string;
  country: Country;
  language: Language;
  relation: string = "oneToOne";
  cols: any = [];
  protocolSiteAttributeLength;
  protocolSiteRelation: any;
  siteProtocolSiteRelation: any;
  siteDeliveryAddressProtocolSiteRelation: any;
  attributeValues: any = {};
  private documentDetailsMap = new Map<string, any>();
  nextBtnLabel;
  previousBtnLabel;
  label: string;
  initialLabel: string = this.cs.getLabel("lbl_details");
  saveProtocolSiteBtnLabel: string;
  isFirstTabSaved: boolean = false;
  isNew: boolean = true;
  prefixToAttachForButtonIdToUniquelyIdentifyButton = AppSettings.BUTTON_PREFIX
  tabCode;
  tabIndex;
  @Input() protocolEntityId: string;
  @Input() protocolSiteId: string;
  @Input() isChangeReasonNotRequired: boolean = false;
  @Input() isEditNotAllowedSpecificStatus: boolean = false;
  isLoading: boolean = true;
  protocolAttributeId: string;
  siteStatusAttributeId: string;
  siteDefaultSortColumnAttributeId: string;
  siteId: string;
  siteDeliveryAddressId: string;
  siteAddresOwnerAttributeId: string;
  siteAddressStatusAttributeId: string;
  headerLabel: string;

  @ViewChild(MiFormComponent) miFormComponent: MiFormComponent;
  @Output() saveSuccess = new EventEmitter<void>();

  constructor(
    private entityService: EntityService,
    private cs: CommonBindingDataService,
    private messageService: MessageService,
    private router: Router,
    private route: ActivatedRoute,
    private configService: ConfigService,
    private metaDataService: MetaDataService,
    private restApiService: RestApiService,
  ) { }

  ngOnInit() {
    this.initializeLabels();
    this.initializeEntityService();
    this.reset();

    if (this.protocolSiteId) {
      this.loadProtocolSiteData();
    } else {
      this.getSiteStatusAttributeId();
    }
    this.setRoutePath('details');
    this.isFirstTabSaved = false;
    this.activeIndex = 0;
  }

  private initializeLabels() {
    this.nextBtnLabel = this.protocolSiteId ?
      this.cs.getLabel("protocol.lbl_update_protocol_site") :
      this.cs.getLabel("protocol.lbl_add_protocol_site");
    this.previousBtnLabel = this.cs.getLabel("lbl_previous");
  }

  private initializeEntityService() {
    this.moduleName = AppSettings.ENTITY_CODE.PROTOCOL_SITE;
    this.country = JSON.parse(localStorage.getItem(AppSettings.COUNTRY));
    this.language = JSON.parse(localStorage.getItem(AppSettings.LANGUAGE));
    this.entityService.setRelatedData([]);
    this.documentDetailsMap.clear();
  }

  private loadProtocolSiteData() {
    this.isLoading = true;
    if (this.protocolSiteId) {
      this.entityService
        .getEntity(this.protocolSiteId, AppSettings.ENTITY_CODE.PROTOCOL_SITE, AppSettings.VIEW_CODE.ADD_EDIT_VIEW)
        .subscribe({
          next: (res) => {
            if (res) {
              this.processProtocolSiteResponse(res);
              this.isNew = false;
              const currentTab = this.protocolSiteAttributeData?.tabs[this.activeIndex];
              if (currentTab) {
                this.setRoutePath(currentTab.tabCode);
              }
            }
            this.isLoading = false;
          },
          error: () => {
            this.isLoading = false;
          }
        });
    } else {
      this.getSiteStatusAttributeId();
    }
  }

  private processProtocolSiteResponse(data: any) {
    this.attributeValues = this.cs.getOrgAttributeValues(data);
    this.normalizeDateFields();

    const siteData = data.relatedData.find(item => item.entityCode === AppSettings.ENTITY_CODE.SITE);
    const siteEntityData = this.cs.getOrgAttributeValues(siteData) as any;
    const siteAttributeId = this.attributeValues.site_entity_id;
    this.attributeValues.site_entity_id = { name: siteEntityData.site_name, id: siteAttributeId };

    const siteDeliveryAddressData = data.relatedData.find(item => item.entityCode === AppSettings.ENTITY_CODE.SITE_DELIVERY_ADDRESS);
    if (siteDeliveryAddressData) {
      const siteDeliveryAddressEntityData = this.cs.getOrgAttributeValues(siteDeliveryAddressData);
      const siteDeliveryAddressAttributeId = this.attributeValues.site_delivery_address_id;
      this.attributeValues.site_delivery_address_id = { name: this.createName(siteDeliveryAddressEntityData), id: siteDeliveryAddressAttributeId };
    }
    this.processRelatedData(data.relatedData);
    this.getSiteStatusAttributeId();
  }

  private normalizeDateFields() {
    const dateFields = ['commencement_date', 'next_visit', 'pause_from_date', 'pause_to_date'];
    for (const dateField of dateFields) {
      if (this.attributeValues[dateField]) {
        const updatedDate = this.metaDataService.setDateInTimezone(this.attributeValues[dateField] as number);
        this.attributeValues[dateField] = new Date(updatedDate as string | number);
      }
    }

    if (this.attributeValues.commencement_date) {
      this.cs.emitDateValue(this.attributeValues.commencement_date as Date);
    }

    if (this.attributeValues.visit_frequency) {
      this.cs.emitDaysValue(this.attributeValues.visit_frequency as number);
    }

    if (this.attributeValues.pause_from_date) {
      // Ensure pause dates are properly timezone-adjusted
      const pauseFromDate = this.attributeValues.pause_from_date as Date;
      pauseFromDate.setHours(0, 0, 0, 0);
      this.cs.emitPauseFromDate(pauseFromDate);
    }

    if (this.attributeValues.pause_to_date) {
      // Ensure pause dates are properly timezone-adjusted
      const pauseToDate = this.attributeValues.pause_to_date as Date;
      pauseToDate.setHours(0, 0, 0, 0);
      this.cs.emitPauseToDate(pauseToDate);
    }
  }

  createName(item) {
    const parts = [
      item.address_line_1,
      item.address_line_2,
      item.address_short_name,
      item.city,
      item.state,
      item.street,
      item.zip
    ].filter(part => part && part.trim());
    return parts.join(' ');
  }

  private processRelatedData(relatedData: any[]) {
    if (relatedData.length > 0) {
      const organizedData: { [key: string]: any[] } = {};
      relatedData.forEach(item => {
        if (item.actionStatus === "DELETED") {
          return;
        }
        const entityCode = item.entityCode;
        if (!organizedData[entityCode]) {
          organizedData[entityCode] = [];
        }
        const entityData = this.cs.getOrgAttributeValues(item) as any;
        entityData.entityId = item.entityId;
        organizedData[entityCode].push(entityData);
      });
      this.entityService.setRelatedData(organizedData);
    }
  }

  onTabChange(event: any) {
    const currentTab = this.protocolSiteAttributeData.tabs[this.activeIndex];
    if (currentTab) {
      this.setRoutePath(currentTab.tabCode);
    }
    if (event.index === 0) {
      this.isFirstTabSaved = false;
    }
    if (!this.isFirstTabSaved && event.index > 0) {
      this.activeIndex = 0;
      this.messageService.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Please save details in first tab before proceeding'
      });
      return;
    }

    this.label = event.originalEvent.target.innerText.trim();
  }

  getAttributes() {
    const entityType = AppSettings.ENTITY_CODE.PROTOCOL_SITE;
    const viewCode = AppSettings.VIEW_CODE.ADD_EDIT_VIEW;

    this.entityService.getAttributeDefinition(entityType, viewCode).subscribe({
      next: (res) => {
        if (!res) {
          this.isLoading = false;
          return;
        }
        this.setProtocolSiteView(res);
        const currentTab = this.protocolSiteAttributeData.tabs[this.activeIndex];
        if (currentTab) {
          this.setRoutePath(currentTab.tabCode);
        }
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  private toFileIdArray(value: any): string[] {
    if (!value) return [];
    const arr: any[] = Array.isArray(value)
      ? [].concat(...value)
      : [String(value)];
    return arr.filter(Boolean).map(String);
  }

  private fetchFileDetailsIfNeededThenGetAttributes() {
    const fileIds = this.toFileIdArray(this.attributeValues.upload_document);
    if (fileIds.length > 0) {
      this.restApiService.post(
        'get site document details for edit',
        `${this.configService.getLoggedInTenantCode()}/api/v1/files/details`,
        { forTenantCode: this.configService.getForTenantCode(), uploadType: AppSettings.DOCUMENTS_TYPE.DOCUMENT, fileIds }
      ).subscribe({
        next: (fileDetails: any) => {
          if (Array.isArray(fileDetails)) {
            fileDetails.forEach(f => {
              if (f.fileId) this.documentDetailsMap.set(f.fileId, f);
              if (f.resourceId) this.documentDetailsMap.set(f.resourceId, f);
            });
          }
          this.getAttributes();
        },
        error: () => this.getAttributes()
      });
    } else {
      this.getAttributes();
    }
  }

  private getSiteStatusAttributeId() {
    this.entityService.getAttributeDefinition(AppSettings.ENTITY_CODE.SITE, AppSettings.VIEW_CODE.ADVANCED_FILTER_VIEW)
      .subscribe(filterResponse => {
        if (filterResponse) {
          const siteStatusAttribute = this.cs.getOrganizedAttribute(filterResponse)?.tabs[0]?.groups[0]?.fields?.find(
            element => element.attributeCode === 'site_status')?.attributeId;
          if (siteStatusAttribute) {
            this.siteStatusAttributeId = siteStatusAttribute;

            this.entityService.getAttributeDefinition(AppSettings.ENTITY_CODE.SITE, AppSettings.VIEW_CODE.DEFAULT_SORT_VIEW)
              .subscribe(sortColumnResponse => {
                if (sortColumnResponse) {
                  this.siteDefaultSortColumnAttributeId = this.cs.getColumns(sortColumnResponse)?.find(element => element.attributeCode === 'site_name')?.attributeId;
                }
              });
            this.entityService.getAttributeDefinition(AppSettings.ENTITY_CODE.SITE_DELIVERY_ADDRESS, AppSettings.VIEW_CODE.ADVANCED_FILTER_VIEW)
              .subscribe(deliveryAddressFilterResponse => {
                if (deliveryAddressFilterResponse) {
                  this.siteAddresOwnerAttributeId = _.find(deliveryAddressFilterResponse?.relations, { otherEntityCode: 'site', relation: "oneToOne" }).ownerAttributeId;
                  this.siteAddressStatusAttributeId = this.cs.getColumns(deliveryAddressFilterResponse)?.find(element => element.attributeCode === 'delivery_status')?.attributeId;
                  this.fetchFileDetailsIfNeededThenGetAttributes();
                }
              });
          }
        }
      });
  }

  setProtocolSiteView(res) {
    this.attributeLabels = this.cs.getAttributeLabels(res);
    this.originalProtocolSiteAttributeData = this.cs.getOrganizedAttribute(res);
    this.protocolAttributeId = this.originalProtocolSiteAttributeData?.tabs[0]?.groups[0]?.fields?.find(
      element => element.attributeCode === 'protocol_entity_id')?.attributeId;

    this.protocolSiteAttributeData = _.cloneDeep(this.originalProtocolSiteAttributeData);

    this.protocolSiteAttributeData.tabs = this.protocolSiteAttributeData.tabs.map(tab => ({
      ...tab,
      groups: tab.groups.map(group => ({
        ...group,
        fields: group.fields.filter(field => {
          if (field.attributeCode === 'protocol_entity_id') {
            return false;
          }
          if (field.attributeCode === 'site_entity_id') {
            field.moduleName = AppSettings.ENTITY_CODE.SITE;
            field.filter = [{
              attributeId: this.siteStatusAttributeId,
              attributeValue: "Active"
            }];
            field.defaultSortColumn = this.siteDefaultSortColumnAttributeId;
            if (this.protocolSiteId) {
              field.disabled = true;
            }
            return true;
          }
          if (field.attributeCode === 'site_delivery_address_id') {
            field.moduleName = AppSettings.ENTITY_CODE.SITE_DELIVERY_ADDRESS;
            field.filter = [{
              attributeId: this.siteAddresOwnerAttributeId,
              attributeValue: ""
            }, {
              attributeId: this.siteAddressStatusAttributeId,
              attributeValue: "Active"
            }];
            return true;
          }
          if (field.attributeCode === 'change_reason') {
            if (this.isChangeReasonNotRequired) {
              return false;
            } else {
              if (field.validation) {
                (field.validation as { required: boolean }).required = true;
              }
              return true;
            }
          }

          if (field.attributeCode === 'upload_document') {
            const fileIds = this.toFileIdArray(this.attributeValues.upload_document);
            field.documentURLs = fileIds.map(id => this.documentDetailsMap.get(id)?.signedUrl).filter(Boolean);
            field.documentNames = fileIds.map(id => this.documentDetailsMap.get(id)?.originalFileName || 'Document');
          }

          if (field.attributeCode === 'next_visit') {
            return {
              ...field,
              disabled: true
            };
          }

          return true;
        })
      }))
    }));

    this.protocolSiteAttributeData.tabs = this.cs.setDependsOnAttributeCode(this.protocolSiteAttributeData?.tabs);
    this.tabIndex = this.protocolSiteAttributeData.tabs;
    this.tabIndex.forEach(tab => {
      this.setRoutePath(tab.tabCode);
    });
    this.protocolSiteAttributeLength = this.protocolSiteAttributeData.tabs.length;
    this.protocolSiteRelation = _.find(res?.relations, { otherEntityCode: 'protocol', relation: "oneToOne" });
    this.siteProtocolSiteRelation = _.find(res?.relations, { otherEntityCode: 'site', relation: "oneToOne" });
    this.siteDeliveryAddressProtocolSiteRelation = _.find(res?.relations, { otherEntityCode: 'site_delivery_address', relation: "oneToOne" });

    if (this.protocolSiteId) {
      setTimeout(() => {
        this.initializeFormWithExistingValues();
        this.triggerNextVisitRecalculation();
      }, 100);
    }
  }

  private initializeFormWithExistingValues() {
    if (this.miFormComponent && this.miFormComponent.miFormGroup) {
      const form = this.miFormComponent.miFormGroup;

      if (this.attributeValues.commencement_date && form.get('commencement_date')) {
        form.get('commencement_date').setValue(this.attributeValues.commencement_date);
      }

      if (this.attributeValues.visit_frequency && form.get('visit_frequency')) {
        form.get('visit_frequency').setValue(this.attributeValues.visit_frequency);
      }

      if (this.attributeValues.next_visit && form.get('next_visit')) {
        form.get('next_visit').setValue(this.attributeValues.next_visit);
      }

      if (this.attributeValues.pause_from_date && form.get('pause_from_date')) {
        form.get('pause_from_date').setValue(this.attributeValues.pause_from_date);
      }

      if (this.attributeValues.pause_to_date && form.get('pause_to_date')) {
        form.get('pause_to_date').setValue(this.attributeValues.pause_to_date);
      }
    }
  }

  private triggerNextVisitRecalculation() {
    if (this.attributeValues.commencement_date && this.attributeValues.visit_frequency) {
      this.cs.emitDateValue(this.attributeValues.commencement_date as Date);
      this.cs.emitDaysValue(this.attributeValues.visit_frequency as number);
    }
  }

  onPreviousClick() {
  }

  setRoutePath(tabCode: any) {
    const routeLabel = this.protocolSiteId ? this.cs.getLabel("protocol.lbl_edit_site") : this.cs.getLabel("protocol.lbl_add_site");
    if (!tabCode && this.protocolSiteAttributeData?.tabs?.length > 0) {
      tabCode = this.protocolSiteAttributeData.tabs[0].tabCode;
    }

    this.routePath = [
      {
        label: `${routeLabel}`,
        routerLink: '/app/protocols',
        icon: "pi pi-arrow-left",
        iconStyle: { "font-weight": "bold", "margin-right": "10px" },
      }
    ];
    if (tabCode) {
      this.routePath.push({
        label: `${tabCode}`,
        styleClass: 'breadcrumb-child forward-slash breadcrumb-text',
        style: { 'display': 'flex', 'top': '2px', 'position': 'relative' }
      });
    }
  }

  onSaveProtocolSite(event) {
    const protocolSiteData = {
      forTenantCode: this.configService.getForTenantCode(),
      entityCode: this.moduleName,
      countryCode: this.country[0].countryCode,
      languageCode: this.language?.[0]?.langCode || 'en',
      data: [],
      relationshipData: []
    };

    const pData = event;
    if (pData.can_manage_patient === null || pData.can_manage_patient === undefined) {
      pData.can_manage_patient = false;
    }
    for (const [key, value] of Object.entries(pData)) {
      if (value || (key === 'can_manage_patient' && value === false)) {
        let attributeValue = value;
        if (key === 'can_manage_patient') {
          attributeValue = value;
        }
        if (key === 'site_entity_id' && typeof value === 'object' && value !== null && 'id' in value) {
          attributeValue = (value as any).id;
        }
        if (key === 'site_delivery_address_id' && typeof value === 'object' && value !== null && 'id' in value) {
          attributeValue = (value as any).id;
        }
        if (key === 'upload_document') {
          attributeValue = this.toFileIdArray(value);
        }
        if (attributeValue || (key === 'can_manage_patient' && attributeValue === false)) {
          protocolSiteData.data.push({ attributeCode: key, attributeValue });
        }
      }
    }

    protocolSiteData.data.push({
      attributeId: this.protocolAttributeId,
      attributeValue: this.protocolEntityId
    });

    this.siteId = protocolSiteData.data.find(item => item.attributeCode === 'site_entity_id')?.attributeValue;
    this.siteDeliveryAddressId = protocolSiteData.data.find(item => item.attributeCode === 'site_delivery_address_id')?.attributeValue;
    protocolSiteData.relationshipData?.push({
      entityRelationshipConfigId: this.protocolSiteRelation.entityRelationshipConfigId,
      otherEntityId: this.protocolEntityId
    }, {
      entityRelationshipConfigId: this.siteProtocolSiteRelation.entityRelationshipConfigId,
      otherEntityId: this.siteId
    }, {
      entityRelationshipConfigId: this.siteDeliveryAddressProtocolSiteRelation.entityRelationshipConfigId,
      otherEntityId: this.siteDeliveryAddressId
    });

    const entitiesData = {
      countryCode: this.country[0].countryCode,
      tenantCode: this.configService.getForTenantCode()
    };

    if (!this.protocolSiteId) {
      this.entityService
        .createEntities(this.moduleName, entitiesData)
        .subscribe((res: any) => {
          this.saveProtocolSiteData(protocolSiteData, res.entityId, true);
        });
    } else {
      this.saveProtocolSiteData(protocolSiteData, this.protocolSiteId, false);
    }
  }

  saveProtocolSiteData(protocolSiteData, entityId: string, isNew: boolean) {
    this.isNew = isNew;
    protocolSiteData.data = this.cs.mapAttributeIds(protocolSiteData.data, this.originalProtocolSiteAttributeData.tabs);
    this.entityService
      .saveAttributeData(protocolSiteData.entityCode, entityId, protocolSiteData).subscribe({
        next: () => {
          const success = this.cs.getLabelValue('protocol_site.message.success.create', this.attributeLabels, this.cs.getLabel('protocol.lbl_site_added_successfully'));
          const update = this.cs.getLabelValue('protocol_site.message.success.update', this.attributeLabels, this.cs.getLabel('protocol.lbl_site_updated_successfully'));
          if (this.protocolSiteId) {
            this.messageService.add({ key: 'tst', severity: 'success', summary: 'Success', detail: update });
          } else {
            this.messageService.add({ key: 'tst', severity: 'success', summary: 'Success', detail: success });
          }

          setTimeout(() => {
            this.saveSuccess.emit();
            this.reset();
            // Clear pause dates from service after successful save
            this.cs.clearPauseDates();
          }, 500);
        },
        error: () => {
          if (isNew) {
            this.cs.onDisplayMessage(this.data, 'protocol', AppSettings.MSG_ACTION.ADD, 'error');
          } else {
            this.cs.onDisplayMessage(this.data, 'protocol', AppSettings.MSG_ACTION.UPDATE, 'error');
          }
          this.reset();
        }
      });
  }

  onNextBtnClick() {
    if (this.protocolSiteAttributeData?.tabs && this.protocolSiteAttributeData.tabs.length > 0) {
      const currentTab = this.protocolSiteAttributeData.tabs[this.activeIndex];
      this.setRoutePath(currentTab.tabCode);
      if (currentTab) {
        const btn = document.getElementById(`0_${currentTab.tabCode}`);
        if (btn) {
          btn.click();
        }
      }
    }
  }

  onNextClick() {
    if (this.activeIndex < this.protocolSiteAttributeLength - 1) {
      this.activeIndex++;
    }
    const currentTab = this.protocolSiteAttributeData.tabs[this.activeIndex];
    if (currentTab) {
      this.setRoutePath(currentTab.tabCode);
    }
    if (this.isLastIndex() === true) {
      this.nextBtnLabel = this.saveProtocolSiteBtnLabel;
    }
  }

  getLastIndex(): number {
    return this.protocolSiteAttributeLength - 1;
  }

  isLastIndex(): boolean {
    return this.activeIndex === this.getLastIndex();
  }

  onCancel() {
    this.isFirstTabSaved = false;
    this.miFormComponent.resetForm();
    this.reset();
    // Clear pause dates from service when dialog is cancelled
    this.cs.clearPauseDates();
    this.saveSuccess.emit();
  }

  redirectToListPage() {
    this.router.navigate(["/app/protocols"]);
  }

  reset() {
    this.attributeValues = {};
    this.isFirstTabSaved = false;
    this.activeIndex = 0;
    this.protocolSiteAttributeData = null;
    this.originalProtocolSiteAttributeData = null;
    this.attributeLabels = {};
    this.data = null;
    this.isNew = true;
    this.isLoading = true;
    this.protocolAttributeId = null;
    this.siteStatusAttributeId = null;
    this.siteId = null;
    this.siteDeliveryAddressId = null;
    this.protocolSiteRelation = null;
    this.siteProtocolSiteRelation = null;
    this.siteDeliveryAddressProtocolSiteRelation = null;
    this.protocolSiteAttributeLength = null;
    this.tabIndex = null;
    this.tabCode = null;
    if (this.miFormComponent) {
      this.miFormComponent.resetForm();
    }
  }

  ngOnDestroy() {
    this.reset();
    // Clear pause dates from service when component is destroyed
    this.cs.clearPauseDates();
  }

} 